// The 401 → login redirect, and the one-shot guard that stops it looping (busy-my-web#321).
//
// busy-my-web is a Duende BFF cookie app: the browser never holds a token, so "signed in" means
// the `bff` cookie plus the BFF's server-side session, and the access token is refreshed
// server-side on the first proxied call that needs one. Two credentials with two lifetimes sit
// behind that, and they can disagree:
//
//   • the cookie is alive (30d sliding) but the refresh token is not — the >90d absolute case, or
//     an admin revocation. `/bff/user` still answers 200 from the cookie's claims, the app boots,
//     and then the first proxied API call 401s because the BFF cannot mint an access token.
//
// That half-authenticated boot has to end somewhere, and the designed exit is a redirect to
// `/bff/login` (the interceptor's existing behaviour, kept). The trap is the case after it: if the
// IdP session is still valid, `/bff/login` completes SILENTLY and hands back a session that still
// cannot produce tokens — so the next API call 401s, redirects again, and the user watches the
// page flash between the app and the identity provider forever.
//
// So a redirect is allowed ONCE per window. A second 401 inside it is answered by clearing the
// user and letting the page show its logged-out state — an honest dead end beats a loop.
//
// The state is a timestamp in `sessionStorage`, exactly like the router's stale-deploy guard
// (`CHUNK_RELOAD_KEY`, src/router/index.js): per tab, gone when the tab is, and cleared by
// `sessionStorage.clear()` in `auth.signOut()` without that function having to know about it.
//
// Pure and injected — the storage and the clock are arguments — so every branch is answerable
// without a browser, a component or a real 401.

/** The sessionStorage key. Namespaced like `busy.chunkReload`, and cleared by sign-out's wipe. */
export const AUTH_REDIRECT_KEY = 'busy.authRedirect'

/**
 * How long one redirect stands for.
 *
 * 60s is far longer than a real login round trip (a silent re-login is a few hundred ms) and far
 * shorter than any session a person would notice: a genuine second expiry, minutes or hours later,
 * is a fresh problem and gets its own redirect.
 */
export const REDIRECT_WINDOW_MS = 60_000

// The fallback clock for a browser that will not give us storage at all (Safari private mode
// throws on `sessionStorage`). Module-level so the guard still holds within the page's lifetime —
// without it, "cannot remember" would mean "redirect every time", which is the loop itself.
let lastRedirectAt = 0

/** Test seam and sign-out's completeness: forget the in-memory fallback. */
export function resetLoginRedirectMemory() {
  lastRedirectAt = 0
}

/**
 * When the last redirect was, in epoch ms, or 0 for "none that we know of".
 *
 * A storage that throws, or a value that is not a number (hand-edited, or written by an older
 * build), is read as 0: unknown history falls back to the in-memory stamp rather than to a
 * pretended one.
 */
export function lastLoginRedirectAt(storage) {
  try {
    const raw = storage?.getItem?.(AUTH_REDIRECT_KEY)
    const at = Number(raw)
    if (Number.isFinite(at) && at > 0) return at
  } catch {
    /* storage unavailable — the in-memory stamp below is all we have */
  }
  return lastRedirectAt
}

/**
 * May we send the user to the identity provider right now?
 *
 * True when nothing has been tried, or when the last attempt is older than the window — including
 * a stamp from the FUTURE (a clock that moved backwards is not evidence of a recent redirect, and
 * treating it as one would strand the user on a logged-out page they cannot leave).
 */
export function loginRedirectAllowed(storage, now = Date.now()) {
  const at = lastLoginRedirectAt(storage)
  if (!at) return true
  return at > now || now - at >= REDIRECT_WINDOW_MS
}

/** Remember that we just sent the user to log in. */
export function markLoginRedirect(storage, now = Date.now()) {
  lastRedirectAt = now
  try {
    storage?.setItem?.(AUTH_REDIRECT_KEY, String(now))
  } catch {
    /* the in-memory stamp still holds the guard for this page */
  }
}

/**
 * Forget it — the session is demonstrably working again.
 *
 * Called from `probeSession`'s SESSION_ACTIVE branch: a `/bff/user` that answers with real claims
 * is the only proof that the loop this guard exists for is over, and without this a user who
 * signed in normally would be one 401 away from being refused their single redirect.
 */
export function clearLoginRedirect(storage) {
  lastRedirectAt = 0
  try {
    storage?.removeItem?.(AUTH_REDIRECT_KEY)
  } catch {
    /* nothing to clear */
  }
}

/**
 * The decision and the record in one call, because they must not come apart: every "yes" here is
 * a redirect that has been counted.
 *
 * @returns {boolean} true → redirect now; false → we already did, show the logged-out state.
 */
export function consumeLoginRedirect(storage, now = Date.now()) {
  if (!loginRedirectAllowed(storage, now)) return false
  markLoginRedirect(storage, now)
  return true
}

/**
 * WHERE to send them.
 *
 * The BFF's own `Location` header wins when it sends one — it knows the `returnUrl` it wants and
 * carries no token material — and the app falls back to building the same URL itself. A hint that
 * is not a string (a header object, an array) is ignored rather than coerced into a nonsense URL.
 */
export function loginRedirectUrl({ hinted = '', bffBaseUrl = '', href = '' } = {}) {
  const hint = typeof hinted === 'string' ? hinted.trim() : ''
  if (hint) return hint
  return `${bffBaseUrl}/bff/login?returnUrl=${encodeURIComponent(href)}`
}

/**
 * `window.sessionStorage`, or null where it cannot be touched — reading the property itself throws
 * in some privacy modes, which is why this is a function and not a constant.
 */
export function sessionStore() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  } catch {
    return null
  }
}

export default consumeLoginRedirect
